import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { IntlShape } from '@kyruus/intl';
import Tooltip from '@kyruus/tooltip';

import { MoreTooltipContent } from '../../components/ProviderHighlights/styles';
import { debounce } from '../utils';
import messages from '../messages';
import { TrackingFunctionContext } from '../contexts';
import { TRACKING_EVENTS } from '../constants';

export { useProfileSections } from './useProfileSections';
export { useProfileAvailability } from './useProfileAvailability';
export { useProfileCtaModal } from './useProfileCtaModal';
export { useTracking } from './useTracking';

/**
 * Checks if a given media query matches (https://usehooks-ts.com/react-hook/use-media-query)
 * useMediaQuery('(min-width: 768px)')
 * @param {string} `query` the media query to match
 * @returns {boolean}
 */
export const useMediaQuery = (query: string): boolean => {
  const getMatches = (query: string): boolean => {
    // Prevents SSR issues
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function'
    ) {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState<boolean>(getMatches(query));

  function handleChange() {
    setMatches(getMatches(query));
  }

  useEffect(() => {
    const matchMedia =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query)
        : null;

    if (matchMedia === null) {
      return;
    }

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Listen matchMedia
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return matches;
};

/**
 * Trims a list of information card items down to fit within the card container
 * @param {string[]} `items` the items to trim
 * @param {React.ReactNode} `filterMatchClearButton` the button to clear a filter match icon (for inclusion in the list of items)
 * @param {IntlShape} `intl` the IntlShape object from react-intl
 * @returns { checkMarkIcon: React.ReactElement | null, filterMatchClearButton: React.ReactElement | null }
 */
export const useOverflowTrimList = (
  items: string[] | null,
  intl: IntlShape
): {
  listRef: React.RefObject<HTMLUListElement>;
  renderItems: Array<React.ReactNode | string>;
} => {
  const totalCount = Array.isArray(items) ? items.length : 0;
  const [renderCount, setRenderCount] = useState(totalCount);
  const [windowWidth, setWindowWidth] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // track component mounted state so that debounce resize handler doesn't fire when component is unmounted
  useLayoutEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // decrement the count of rendered items until width of <ul> is within width of container
  useLayoutEffect(() => {
    if (listRef.current) {
      const { clientWidth, scrollWidth } = listRef.current;

      if (scrollWidth > clientWidth) {
        setRenderCount((current) => current - 1);
      }
    }
  }, [renderCount, windowWidth]);

  // add window size listener
  useEffect(() => {
    const resizeListener = debounce(() => {
      if (isMountedRef.current) {
        // reset render count to kick off another trim
        setRenderCount(totalCount);
        setWindowWidth(window.innerWidth);
      }
    }, 300);

    setWindowWidth(window.innerWidth);
    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [totalCount]);

  // reset render count if the items list size changes
  useEffect(() => {
    setRenderCount(totalCount);
  }, [totalCount]);

  // include the filter match clear button in the render items
  const trimmedItems = Array.isArray(items) ? items.slice(0, renderCount) : [];
  const renderItems = trimmedItems;
  const hiddenItems = Array.isArray(items)
    ? items.slice(renderCount, totalCount)
    : [];
  const allItemsHidden = hiddenItems.length === totalCount;
  const moreTooltip = (
    <Tooltip title={<>{hiddenItems.join(', ')}</>} data-testid="more-tooltip">
      <MoreTooltipContent
        tabIndex={0}
        aria-label={`${intl.formatMessage(messages.ariaMoreTooltipText, {
          itemLength: hiddenItems.length,
          items: hiddenItems.join(', ')
        })}`}
      >
        {`${intl.formatMessage(messages.informationCardMoreTooltipText, {
          itemsCount: hiddenItems.length,
          tooltipText: allItemsHidden
            ? intl.formatMessage(messages.informationCardTooltipTextItems)
            : intl.formatMessage(messages.informationCardTooltipTextMore)
        })}`}
      </MoreTooltipContent>
    </Tooltip>
  );
  const renderItemsWithTooltip = [...renderItems, moreTooltip];

  return {
    listRef,
    renderItems: hiddenItems.length ? renderItemsWithTooltip : renderItems
  };
};

export const useTrackBoundaryError = (
  component: string
): ((error: Error, info: { componentStack: string }) => void) => {
  const trackingFunction = useContext(TrackingFunctionContext);

  return (error: Error, info: { componentStack: string }) => {
    trackingFunction(TRACKING_EVENTS.BOUNDARY_ERROR, {
      component,
      error: error.toString(),
      info
    });
  };
};
